import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Activity, AlertTriangle, BarChart3, CalendarCheck, CheckCircle2, ChevronDown, Database, Eye, Mail, MousePointerClick, RefreshCw, Server, ShieldAlert, ShieldCheck, CreditCard, HardDrive, Filter } from "lucide-react";

const LEVELS = [
  { value: "", label: "Tous niveaux" },
  { value: "info", label: "Info" },
  { value: "warning", label: "Warning" },
  { value: "error", label: "Error" },
  { value: "critical", label: "Critical" },
];

const CATEGORIES = [
  { value: "", label: "Toutes catégories" },
  { value: "payment", label: "Paiement" },
  { value: "email", label: "Email" },
  { value: "admin", label: "Admin" },
  { value: "community", label: "Communauté" },
  { value: "formation", label: "Formation" },
  { value: "system", label: "Système" },
  { value: "upload", label: "Upload" },
  { value: "auth", label: "Connexion/admin" },
  { value: "security", label: "Sécurité" },
];

const HANDLED = [
  { value: "open", label: "À traiter" },
  { value: "all", label: "Tous" },
  { value: "handled", label: "Traités" },
];

const levelStyles = {
  info: "bg-blue-50 text-blue-700 border-blue-100",
  warning: "bg-amber-50 text-amber-700 border-amber-100",
  error: "bg-red-50 text-red-700 border-red-100",
  critical: "bg-red-100 text-red-800 border-red-200",
};

const healthIcons = {
  api: Server,
  mongodb: Database,
  stripe: CreditCard,
  smtp: Mail,
  storage: HardDrive,
};

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

export default function AdminSystemLogs() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState({});
  const [health, setHealth] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [testing, setTesting] = useState(false);
  const [filters, setFilters] = useState({ level: "", category: "", handled: "open" });
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const params = useMemo(() => {
    const query = { limit: 150 };
    if (filters.level) query.level = filters.level;
    if (filters.category) query.category = filters.category;
    if (filters.handled === "open") query.handled = false;
    if (filters.handled === "handled") query.handled = true;
    return query;
  }, [filters]);

  const apiGetWithFallback = async (primaryUrl, fallbackUrl, options = {}) => {
    try {
      return await api.get(primaryUrl, options);
    } catch (primaryError) {
      const status = primaryError?.response?.status;
      if (status === 404 && fallbackUrl) {
        return api.get(fallbackUrl, options);
      }
      throw primaryError;
    }
  };

  const load = async () => {
    setLoading(true);
    setError("");

    const [logsResult, healthResult, funnelResult] = await Promise.allSettled([
      apiGetWithFallback("/admin/system/logs", "/admin/system-logs", { params }),
      apiGetWithFallback("/admin/system/health", "/admin/system-health"),
      apiGetWithFallback("/admin/diagnostic-funnel", "/admin/diagnostic-stats"),
    ]);

    if (logsResult.status === "fulfilled") {
      const data = logsResult.value?.data || {};
      setLogs(Array.isArray(data.logs) ? data.logs : []);
      setSummary(data.summary || {});
    } else {
      console.error("Erreur logs système", logsResult.reason);
      setLogs([]);
      setSummary({});
    }

    if (healthResult.status === "fulfilled") {
      setHealth(healthResult.value?.data || null);
    } else {
      console.error("Erreur santé système", healthResult.reason);
      setHealth(null);
    }

    if (funnelResult.status === "fulfilled") {
      setFunnel(funnelResult.value?.data || null);
    } else {
      console.error("Erreur tunnel diagnostic", funnelResult.reason);
      setFunnel(null);
    }

    if ([logsResult, healthResult, funnelResult].some((r) => r.status === "rejected")) {
      setError("Une partie des logs ou de la santé système est temporairement indisponible. Les routes de secours sont utilisées quand elles existent.");
    }

    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, [params]);  // eslint-disable-line react-hooks/exhaustive-deps

  const runSystemTests = async () => {
    setTesting(true);
    setError("");
    try {
      let res;
      try {
        res = await api.post("/admin/system/health/run-tests");
      } catch (err) {
        if (err?.response?.status === 404) {
          res = await api.post("/admin/system-health/run-tests");
        } else {
          throw err;
        }
      }
      setHealth(res.data || null);
      await load();
    } catch (err) {
      console.error("Impossible de lancer les tests système", err);
      setError("Impossible de lancer les tests système pour le moment.");
    } finally {
      setTesting(false);
    }
  };

  const notifyAdminBadgeRefresh = () => {
    window.dispatchEvent(new CustomEvent("sf-menu-counts-refresh"));
  };

  const markHandled = async (logId, handled = true) => {
    try {
      try {
        await api.patch(`/admin/system/logs/${logId}/handled`, { handled });
      } catch (err) {
        if (err?.response?.status === 404) {
          await api.patch(`/admin/system-logs/${logId}/handled`, { handled });
        } else {
          throw err;
        }
      }
      await load();
      notifyAdminBadgeRefresh();
    } catch (err) {
      console.error("Impossible de traiter le log", err);
      alert("Impossible de mettre à jour ce log pour le moment.");
    }
  };

  const markAllHandled = async () => {
    const confirmMessage = filters.level === "info"
      ? "Les logs info ne nécessitent pas de traitement. Aucun changement ne sera appliqué."
      : "Marquer tous les événements à problème visibles comme traités ?";

    if (filters.level === "info") {
      alert(confirmMessage);
      return;
    }

    if (!window.confirm(confirmMessage)) return;

    try {
      const query = {};
      if (filters.level) query.level = filters.level;
      if (filters.category) query.category = filters.category;

      try {
        await api.patch("/admin/system/logs/mark-all-handled", null, { params: query });
      } catch (err) {
        if (err?.response?.status === 404) {
          await api.patch("/admin/system-logs/mark-all-handled", null, { params: query });
        } else {
          throw err;
        }
      }
      await load();
      notifyAdminBadgeRefresh();
    } catch (err) {
      console.error("Impossible de traiter tous les logs", err);
      alert("Impossible de marquer tous les logs comme traités pour le moment.");
    }
  };

  const healthChecks = health?.checks || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="admin-system-logs-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <ShieldAlert className="w-8 h-8 text-brand-green" />
            <p className="label">Admin</p>
          </div>
          <h1 className="font-heading text-4xl font-black">Logs & Santé système</h1>
          <p className="text-gray-600 mt-2">Surveille les erreurs, actions sensibles et statuts techniques sans exposer les détails aux apprenants.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-primary inline-flex items-center gap-2" onClick={runSystemTests} disabled={testing || loading}>
            <RefreshCw className={`w-4 h-4 ${testing ? "animate-spin" : ""}`} />
            Relancer un test système
          </button>
          <button type="button" className="btn-secondary inline-flex items-center gap-2" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Kpi label="À traiter" value={summary.open_total || 0} />
        <Kpi label="Critical" value={summary.open_critical || 0} danger />
        <Kpi label="Errors" value={summary.open_errors || 0} danger={summary.open_errors > 0} />
        <Kpi label="Warnings" value={summary.open_warnings || 0} warning />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-8">
        <Kpi label="Visites accueil" value={funnel?.home_visits || 0} icon={Eye} />
        <Kpi label="Clics diagnostic" value={funnel?.cta_clicks || 0} icon={MousePointerClick} />
        <Kpi label="Visites diagnostic" value={funnel?.diagnostic_visits || 0} icon={BarChart3} />
        <Kpi label="Calendrier chargé" value={funnel?.calendar_views || 0} icon={CalendarCheck} />
        <Kpi label="Conversion accueil → diagnostic" value={`${funnel?.home_to_diagnostic_rate || 0}%`} icon={Activity} />
      </div>

      <div className="card p-6 mb-8">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-brand-green" />
          <h2 className="font-heading text-2xl font-bold">Tunnel diagnostic stratégique</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MiniMetric label="Taux clic → diagnostic" value={`${funnel?.click_to_diagnostic_rate || 0}%`} />
          <MiniMetric label="Réservations Zcal trackées" value={funnel?.reservations || 0} />
          <MiniMetric label="Taux réservation" value={`${funnel?.booking_rate || 0}%`} />
        </div>
        {funnel?.note && <p className="text-xs text-gray-500 mt-4">{funnel.note}</p>}
      </div>

      <div className="card p-6 mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-brand-green" />
            <h2 className="font-heading text-2xl font-bold">Santé système</h2>
            {health?.overall && <StatusPill status={health.overall} />}
          </div>
          <p className="text-xs text-gray-500">Dernier test : {formatDate(health?.checked_at)} {health?.real_tests ? "• tests réels" : "• lecture rapide"}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {healthChecks.map((check) => {
            const Icon = healthIcons[check.key] || ShieldCheck;
            return (
              <div key={check.key} className="rounded-2xl border border-gray-100 p-4 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <Icon className="w-5 h-5 text-brand-green" />
                  <StatusPill status={check.status} />
                </div>
                <p className="font-semibold text-gray-900">{check.label}</p>
                <p className="text-sm text-gray-500 mt-1">{check.message}</p>
                {check.detail && <p className="text-xs text-gray-400 mt-2 break-words">{check.detail}</p>}
              </div>
            );
          })}
          {!healthChecks.length && <p className="text-sm text-gray-500">Santé système indisponible.</p>}
        </div>
      </div>


      <div className="card p-6 mb-6">
        <div className="flex items-center gap-2 mb-5">
          <Filter className="w-5 h-5 text-brand-green" />
          <h2 className="font-heading text-2xl font-bold">Filtres</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select label="Niveau" value={filters.level} options={LEVELS} onChange={(value) => setFilters((f) => ({ ...f, level: value }))} />
          <Select label="Catégorie" value={filters.category} options={CATEGORIES} onChange={(value) => setFilters((f) => ({ ...f, category: value }))} />
          <Select label="Statut" value={filters.handled} options={HANDLED} onChange={(value) => setFilters((f) => ({ ...f, handled: value }))} />
        </div>
      </div>

      {error && <div className="rounded-2xl border border-red-100 bg-red-50 text-red-700 p-4 mb-6">{error}</div>}

      <div className="card overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="font-heading text-2xl font-bold">Événements récents</h2>
            <p className="text-sm text-gray-500 mt-1">{logs.length} log(s)</p>
          </div>
          {logs.some((log) => !log.handled && log.level !== "info") && (
            <button type="button" className="btn-primary py-2 px-4 text-sm" onClick={markAllHandled}>
              Tout marquer traité
            </button>
          )}
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">Chargement des logs...</div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Aucun log ne correspond à ces filtres.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {logs.map((log) => {
              const isExpanded = !!expanded[log.log_id];
              return (
                <div key={log.log_id} className="p-5 hover:bg-gray-50/60 transition-colors">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${levelStyles[log.level] || levelStyles.info}`}>{log.level}</span>
                        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">{log.category}</span>
                        {log.level !== "info" && (
                          log.handled ? <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700">traité</span> : <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-orange-50 text-orange-700">à traiter</span>
                        )}
                      </div>
                      <p className="font-semibold text-gray-900">{log.message}</p>
                      <div className="mt-2 text-sm text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                        <span>{formatDate(log.created_at)}</span>
                        {log.user_email && <span>Utilisateur : {log.user_email}</span>}
                        {log.source && <span>Source : {log.source}</span>}
                        {log.request?.path && <span>{log.request.method} {log.request.path}</span>}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {(log.technical_detail || log.metadata || log.request) && (
                        <button type="button" className="btn-secondary py-2 px-3 text-sm inline-flex items-center gap-1" onClick={() => setExpanded((e) => ({ ...e, [log.log_id]: !isExpanded }))}>
                          <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                          Détail
                        </button>
                      )}
                      {log.level !== "info" && (
                        !log.handled ? (
                          <button type="button" className="btn-primary py-2 px-3 text-sm" onClick={() => markHandled(log.log_id, true)}>Marquer traité</button>
                        ) : (
                          <button type="button" className="btn-secondary py-2 px-3 text-sm" onClick={() => markHandled(log.log_id, false)}>Réouvrir</button>
                        )
                      )}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mt-4 rounded-2xl bg-gray-900 text-gray-100 p-4 overflow-auto text-xs leading-relaxed">
                      <pre className="whitespace-pre-wrap">{log.technical_detail || "Aucun détail technique."}</pre>
                      {(log.metadata || log.request) && (
                        <pre className="whitespace-pre-wrap mt-3 text-gray-300">{JSON.stringify({ request: log.request, metadata: log.metadata }, null, 2)}</pre>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="card p-6 mt-8">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <h2 className="font-heading text-2xl font-bold">Derniers échecs critiques</h2>
        </div>
        {health?.recent_failures?.length ? (
          <div className="space-y-3">
            {health.recent_failures.map((failure) => (
              <div key={failure.log_id} className="rounded-2xl border border-red-100 bg-red-50/50 p-4">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <StatusPill status={failure.level === "critical" ? "critical" : "warning"} />
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-white text-gray-700">{failure.category}</span>
                  <span className="text-xs text-gray-500">{formatDate(failure.created_at)}</span>
                </div>
                <p className="font-semibold text-gray-900">{failure.message}</p>
                {failure.technical_detail && <p className="text-xs text-gray-500 mt-1 break-words">{failure.technical_detail}</p>}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">Aucun échec critique ouvert pour le moment.</p>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, danger, warning, icon }) {
  const Icon = icon || (danger ? AlertTriangle : warning ? AlertTriangle : CheckCircle2);
  const color = danger ? "text-red-600" : warning ? "text-amber-600" : "text-brand-green";
  return (
    <div className="card p-5">
      <div className="flex items-center gap-2 text-gray-500 mb-1">
        <Icon className={`w-4 h-4 ${color}`} />
        <p className="label">{label}</p>
      </div>
      <p className={`font-heading text-3xl font-black ${color}`}>{value}</p>
    </div>
  );
}

function MiniMetric({ label, value }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4">
      <p className="label mb-1">{label}</p>
      <p className="font-heading text-2xl font-black text-gray-900">{value}</p>
    </div>
  );
}

function Select({ label, value, options, onChange }) {
  return (
    <label className="block">
      <span className="label block mb-2">{label}</span>
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </label>
  );
}

function StatusPill({ status }) {
  const normalized = status || "unknown";
  const classes = normalized === "ok"
    ? "bg-emerald-50 text-emerald-700 border-emerald-100"
    : normalized === "warning"
      ? "bg-amber-50 text-amber-700 border-amber-100"
      : "bg-red-50 text-red-700 border-red-100";
  return <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${classes}`}>{normalized}</span>;
}
